import { useEffect, useState } from 'react';

const LANDING_DEFERRED_QUERIES = [
  'mixed-recommendations',
  'nearest-sharing-place',
  'matching-requests',
  'your-interest-random',
  'most-popular',
  'favourites',
  'popular-interests',
  'your-interest-general',
] as const;

export type LandingDeferredQuery = (typeof LANDING_DEFERRED_QUERIES)[number];

/**
 * Unlocks lower-priority landing queries one at a time after the first paint.
 * Disabling the loader also resets the sequence so non-home tabs do not keep
 * the landing-page request workload active.
 */
export function useLandingProgressiveLoad({ enabled = true }: { enabled?: boolean } = {}) {
  const [progress, setProgress] = useState({ enabled, count: 0 });
  let currentProgress = progress;
  if (progress.enabled !== enabled) {
    currentProgress = { enabled, count: 0 };
    setProgress(currentProgress);
  }
  const enabledCount = currentProgress.count;

  useEffect(() => {
    let current = 0;
    let elapsedTicks = 0;

    let progressiveLoadInterval: ReturnType<typeof setInterval> | undefined;
    if (enabled) {
      progressiveLoadInterval = setInterval(() => {
        elapsedTicks += 1;
        if (elapsedTicks < 2) return;

        current += 1;
        setProgress({ enabled: true, count: current });
        if (current >= LANDING_DEFERRED_QUERIES.length && progressiveLoadInterval !== undefined) {
          clearInterval(progressiveLoadInterval);
        }
      }, 100);
    }

    return () => {
      if (progressiveLoadInterval !== undefined) clearInterval(progressiveLoadInterval);
    };
  }, [enabled]);

  const isDeferredQueryEnabled = (query: LandingDeferredQuery) => {
    const index = LANDING_DEFERRED_QUERIES.indexOf(query);
    return enabled && index >= 0 && index < enabledCount;
  };

  const needsTags = isDeferredQueryEnabled('your-interest-random') || isDeferredQueryEnabled('your-interest-general');

  return { isDeferredQueryEnabled, needsTags };
}
